import { isZero } from '@studio/models/ptz';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import { CommandDeviceStop } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { resolveDeviceAnalyticsContext } from '#~/analytics/device-context.js';
import { activeMovementDevices, activeMovementTools, lastIntent } from './constants.js';
import type { Stop } from './types.js';
import { locateProxy } from '#~/agent/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { track } from '#~/analytics/track.js';
import type { AnalyticsEvent } from '#~/analytics/types/index.js';
import { StudioError } from '#~/error/index.js';
import { logger } from '#~/logger/index.js';

/**
 * Stop all movement on the specified device.
 *
 * @param deviceId - The ID of the device to be moved.
 * @param vectors - The vectors to be stopped. By default all vectors are stopped.
 */
export const stop: Stop = (deviceId, vectors, options) => {
  try {
    const proxy = locateProxy(deviceId);

    if (!proxy) {
      return;
    }

    const currentIntent = lastIntent.get(deviceId);
    const nextIntent =
      vectors && currentIntent
        ? {
            ...currentIntent,
            pan: vectors.pan ? 0 : currentIntent.pan,
            tilt: vectors.tilt ? 0 : currentIntent.tilt,
            zoom: vectors.zoom ? 0 : currentIntent.zoom
          }
        : undefined;
    const shouldEndMovement =
      activeMovementDevices.has(deviceId) &&
      (!vectors || !currentIntent || !nextIntent || isZero(nextIntent));

    if (!vectors || !nextIntent || isZero(nextIntent)) {
      lastIntent.delete(deviceId);
    } else {
      lastIntent.set(deviceId, nextIntent);
    }

    if (shouldEndMovement) {
      activeMovementDevices.delete(deviceId);
      const toolUsed = options?.toolUsed ?? activeMovementTools.get(deviceId) ?? 'unknown';
      activeMovementTools.delete(deviceId);
      const deviceAnalyticsContext = resolveDeviceAnalyticsContext(deviceId) ?? {
        device_id: deviceId
      };

      void track({
        category: EVENT_CATEGORIES.DEVICE,
        name: EVENT_NAMES.DEVICE_MOVE_END,
        properties: {
          ...deviceAnalyticsContext,
          tool_used: toolUsed
        }
      } as AnalyticsEvent);
    }

    // Compute the mutation.
    const mutation: DeepPartial<CommandDeviceStop> = {
      focus: false,
      id: deviceId,
      panTilt: vectors?.pan !== false || vectors?.tilt !== false,
      zoom: vectors?.zoom !== false
    };

    const mutB = CommandDeviceStop.encode(CommandDeviceStop.fromPartial(mutation)).finish();

    proxy.connection.publish(`direct.authenticated.${proxy.id}.stop`, mutB);
  } catch (cause) {
    logger.error(new StudioError('Unable to stop device movement.', { cause }));
  }
};
